import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps as MaterialDialogProps,
  DialogTitle,
  IconButton,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import makeStyles from '@mui/styles/makeStyles';
import { ContactSchema } from '../../../definitions/schemas';
import { EventProperty } from '../../../tools/Analytics';
import { getOnlyCountries } from '../../PhoneField/helper';
import { MutationErrorResponse } from '../../../graphql/Interface';
import { OrganizationFlagEnum } from '../../../lib/features/FeatureFlag';
import { parseMutationError } from '../../../lib/graphql/Error';
import { pluralizeLabel } from '../../../lib/Language';
import { removeElement, updateElement } from '../../../lib/Array';
import { trackRoomShared } from './analytics';
import { useI18n } from '../../../lib/hook/useI18n';
import FormValidatorHelper from '../../../Services/FormValidatorService';
import PhoneField from '../../PhoneField';
import RoomsInterface from '../../../graphql/Rooms/RoomsInterface';
import SnackbarStore from '../../../stores/observables/SnackbarStore';
import ThemableButton from '../../../theme/components/Button/ThemableButton';
import ThemableTextField from '../../../theme/components/TextField/ThemableTextField';
import useFeatureFlag from '../../../lib/hook/useFeatureFlag';

type DialogProps = Omit<MaterialDialogProps, 'onClose' | 'open'>;

export interface ShareRoomDialogProps {
  actionLabel: React.ReactNode;
  cancelLabel?: string;
  contactId?: string;
  customDescription?: string;
  DialogProps?: DialogProps;
  disableAddMore?: boolean;
  disableDescription?: boolean;
  disableSuccessToast?: boolean;
  emails?: string[];
  forceEmailInput?: boolean;
  label?: string;
  mobileFullNumbers?: string[];
  mobilePhones?: string[];
  onClose: () => void;
  onContactsInvited?: (invitedContacts: ContactSchema[]) => void;
  open: boolean;
  roomId: string;
  title: React.ReactNode;
  trackingProperties: EventProperty;
}
const useStyles = makeStyles(() => ({
  duplicateErrorText: {
    '& .MuiFormHelperText-root.Mui-error': {
      color: 'var(--warning-color-dark)',
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--warning-color-dark)',
      color: 'var(--warning-color-dark)',
    },
    '& .MuiInputLabel-root.Mui-error': {
      color: 'var(--warning-color-dark)',
    },
    '& .MuiFormLabel-asterisk.Mui-error': {
      color: 'var(--warning-color-dark)',
    },
  },
}));

const ShareRoomDialog: React.FC<ShareRoomDialogProps> = (
  props: ShareRoomDialogProps
): JSX.Element => {
  const { t } = useI18n();
  const classes = useStyles();

  const areSmsNotificationsEnabled = useFeatureFlag(
    OrganizationFlagEnum.SMS_NOTIFICATIONS
  );

  const {
    actionLabel,
    cancelLabel,
    contactId,
    customDescription,
    DialogProps,
    disableAddMore = false,
    disableDescription = false,
    disableSuccessToast = false,
    emails: initialEmails,
    forceEmailInput = false,
    label = '',
    mobileFullNumbers: initialMobileFullNumbers,
    mobilePhones: initialMobilePhones,
    onClose,
    onContactsInvited,
    open,
    roomId,
    title,
    trackingProperties,
  } = props;

  const [emails, setEmails] = useState<string[]>(initialEmails || ['']);
  const [duplicateErrorIndexes, setDuplicateErrorIndexes] = useState<number[]>(
    []
  );
  // store the mobile number to display
  const [mobilePhones, setMobilePhones] = useState<string[]>(
    initialMobilePhones || ['']
  );
  // store the mobile number to save
  const [mobileFullNumbers, setMobileFullNumbers] = useState<string[]>(
    initialMobileFullNumbers || ['']
  );

  const enableMobilePhoneInput = !forceEmailInput && areSmsNotificationsEnabled;

  // misc
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // errors
  const [errorIndexes, setErrorIndexes] = useState<number[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const { current: validator } = useRef(new FormValidatorHelper());

  /**
   * Email validator requires specific messages,
   * to avoid displaying `email-${index}` which is the key.
   * Because the validator check has a dynamic amount of email fields.
   */
  const emailFieldsConstraints = {
    messages: {
      email: t('share.modal.validation.email_valid'),
      required: t('share.modal.validation.email_required'),
    },
  };

  /**
   * Mobile Number validator requires specific messages,
   * to avoid displaying `mobilePhone-${index}` which is the key.
   * Because the validator check has a dynamic amount of mobilePhone fields.
   */
  const mobilePhoneFieldsConstraints = {
    messages: {
      phoneFormat: t('share.modal.validation.mobile_number_valid'),
      required: t('share.modal.validation.mobile_number_required'),
    },
  };

  useEffect(() => {
    setEmails(initialEmails || ['']);
    setMobilePhones(initialMobilePhones || ['']);
    setMobileFullNumbers(initialMobileFullNumbers || ['']);
    setSaving(false);
    setSubmitted(false);
    setDuplicateErrorIndexes([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    sendInvitations();
  };

  const checkForDuplicates = () => {
    const emailSet = new Set<string>();
    const duplicates: number[] = [];

    emails.forEach((email, index) => {
      if (emailSet.has(email)) {
        duplicates.push(index);
      }

      emailSet.add(email);
    });

    setDuplicateErrorIndexes(duplicates);
    return duplicates.length === 0;
  };

  const getInvitees = () => {
    if (enableMobilePhoneInput) {
      return mobileFullNumbers.map((mobilePhone) => ({ mobilePhone }));
    }

    return emails.map((email) => ({ email }));
  };

  const sendInvitations = async () => {
    setSubmitted(true);
    if (saving || !roomId || !validator.isValid() || !checkForDuplicates()) {
      return;
    }

    setSaving(true);

    try {
      const invitees = getInvitees();
      const res = await new RoomsInterface(contactId).shareRoom({
        invitees,
        roomId,
      });

      setSaving(false);

      if (onContactsInvited) {
        onContactsInvited(res.shareRoom.contacts || []);
      }

      if (!disableSuccessToast) {
        const successEntityKey = enableMobilePhoneInput ? '_sms' : '';
        SnackbarStore.show(
          pluralizeLabel(
            invitees.length,
            `share.modal.success.${label}${successEntityKey}`
          )
        );
      }

      trackRoomShared(
        trackingProperties,
        invitees.length,
        areSmsNotificationsEnabled,
        !!contactId
      );

      onClose();
    } catch (e) {
      const error = parseMutationError(e as MutationErrorResponse<any>);
      if (typeof error === 'string') {
        SnackbarStore.show(e as string);
      } else {
        const [message, fields] = error;
        if (fields.emails) {
          setErrorIndexes(fields.emails);
          setErrorMessage(message);
        }
      }

      setSaving(false);
    }
  };

  const onAddEntity = (
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setter((prev) => [...prev, '']);
    if (enableMobilePhoneInput) {
      setMobileFullNumbers((prev) => [...prev, '']);
    }
  };

  const onRemoveEntity = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number
  ) => {
    setter((prev) => removeElement(prev, index));
    if (enableMobilePhoneInput) {
      setMobileFullNumbers((prev) => removeElement(prev, index));
    }
    /*
     * Removing server-side errors for email indexes that matched the removed
     * index. The remaining server-side indexes are adjusted to match the
     * new positions by decreasing the target index if the removed email was
     * positioned before the email with error
     */

    setDuplicateErrorIndexes((prev) => prev.filter((i) => i !== index));

    setErrorIndexes((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))
    );
    validator.reset();
  };

  const entityChangeHandler =
    (setter: React.Dispatch<React.SetStateAction<string[]>>, index: number) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.persist();

      setter((prev) => updateElement(prev, index, () => e.target.value));

      setDuplicateErrorIndexes((prev) => prev.filter((i) => i !== index));
      /**
       * Cleaning the server-side error for this email, as it could be getting
       * fixed and we don't want to bother the user with the error message
       */
      setErrorIndexes((prev) => prev.filter((i) => i !== index));
    };

  // event from react-intl-tel-input
  // e should be React.ChangeEvent<HTMLInputElement> but for TS warning needs to be any
  const mobilePhoneChangeHandler = (index: number) => (e: any) => {
    const { number, fullNumber } = e.target.value;

    setMobilePhones((prev) => updateElement(prev, index, () => number));
    setMobileFullNumbers((prev) =>
      updateElement(prev, index, () => fullNumber)
    );
    /**
     * Cleaning the server-side error for this email, as it could be getting
     * fixed and we don't want to bother the user with the error message
     */
    setErrorIndexes((prev) => prev.filter((i) => i !== index));
  };

  const handlerAddMore = () => {
    if (enableMobilePhoneInput) {
      return onAddEntity(setMobilePhones);
    }

    return onAddEntity(setEmails);
  };

  const getLabelPlural = () =>
    pluralizeLabel(emails.length, `share.modal.${label}`);

  const renderDescription = () => {
    if (disableDescription || enableMobilePhoneInput) {
      return;
    }

    return (
      <DialogContentText>
        {customDescription ||
          t('share.modal.share_with', {
            labelPlural: getLabelPlural(),
          })}
      </DialogContentText>
    );
  };

  const renderEmailInputs = (): JSX.Element[] => {
    const deletable = emails.length > 1;

    return emails.map((email, index) => {
      const hasServerError = errorIndexes.includes(index);
      const hasDuplicateError = duplicateErrorIndexes.includes(index);

      validator.form.message(
        `email-${index}`,
        email,
        'required|email',
        emailFieldsConstraints
      );

      let error = validator.getError(`email-${index}`, submitted);
      if (hasServerError) {
        error = errorMessage;
      }
      if (hasDuplicateError) {
        error = t('share.modal.validation.email_duplicate');
      }

      return (
        <Box
          alignItems="center"
          display="flex"
          // eslint-disable-next-line react/no-array-index-key
          key={`share-with-email-${index}`}
          my={2}
        >
          <ThemableTextField
            autoFocus
            className={
              hasDuplicateError ? classes.duplicateErrorText : undefined
            }
            disabled={saving}
            error={!!error}
            fullWidth
            helperText={error}
            label={`${t('share.modal.input.email')} ${index + 1}`}
            onChange={entityChangeHandler(setEmails, index)}
            required
            size="small"
            type="email"
            value={email}
            inputProps={{ autoComplete: 'off' }}
            FormHelperTextProps={{
              'aria-live': error ? 'polite' : 'off',
            }}
          />
          {deletable && (
            <Box pl={1}>
              <IconButton
                onClick={() => onRemoveEntity(setEmails, index)}
                size="small"
                aria-label={`Remove email field ${index + 1}${
                  emails[index] ? ` with value ${emails[index]}` : ''
                }`}
                title={`Remove email field ${index + 1}${
                  emails[index] ? ` with value ${emails[index]}` : ''
                }`}
              >
                <Close />
              </IconButton>
            </Box>
          )}
        </Box>
      );
    });
  };

  const renderPhoneInputs = (): JSX.Element[] => {
    const deletable = mobilePhones.length > 1;

    return mobilePhones.map((mobilePhone, index) => {
      const hasServerError = errorIndexes.includes(index);
      const fullNumber = mobileFullNumbers[index];

      validator.form.message(
        `mobilePhones-${index}`,
        fullNumber,
        'required|phoneFormat',
        mobilePhoneFieldsConstraints
      );

      let error = validator.getError(`mobilePhones-${index}`, submitted);
      if (hasServerError) {
        error = errorMessage;
      }

      return (
        <Box
          alignItems="center"
          display="flex"
          // eslint-disable-next-line react/no-array-index-key
          key={`share-with-mobilePhones-${index}`}
          my={2}
        >
          <PhoneField
            disabled={saving}
            error={!!error}
            fullWidth
            helperText={error}
            label={t('share.modal.input.mobile_number')}
            required
            onChange={mobilePhoneChangeHandler(index)}
            onlyCountries={getOnlyCountries(areSmsNotificationsEnabled)}
            size="medium"
            value={mobilePhone}
          />
          {deletable && (
            <Box pl={1}>
              <IconButton
                onClick={() => onRemoveEntity(setMobilePhones, index)}
                size="small"
              >
                <Close />
              </IconButton>
            </Box>
          )}
        </Box>
      );
    });
  };

  const renderAddMore = () => {
    if (disableAddMore) {
      return;
    }

    return (
      <Box my={2} textAlign="center">
        <ThemableButton
          onClick={handlerAddMore}
          startIcon={<Add />}
          variant="outlined"
        >
          {t('share.modal.add_more')}
        </ThemableButton>
      </Box>
    );
  };

  const renderContent = () => {
    if (enableMobilePhoneInput) {
      return renderPhoneInputs();
    }

    return renderEmailInputs();
  };

  return (
    <Dialog
      {...DialogProps}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
    >
      <form onSubmit={handleSubmit} tabIndex={-1}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {renderDescription()}
          {renderContent()}
          {renderAddMore()}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={onClose}>
            {cancelLabel || t('share.modal.cancel')}
          </Button>
          <ThemableButton
            disabled={saving}
            onClick={handleSubmit}
            type="submit"
            variant="contained"
          >
            {actionLabel}
          </ThemableButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ShareRoomDialog;
